import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  AlertCircle,
  ArrowRight,
  BookOpenCheck,
  FileCheck2,
  FilePlus2,
  FolderLock,
  GraduationCap,
  PackageCheck,
  Search,
  ShieldCheck,
  Trash2,
} from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { AddDocumentDialog } from '../components/eqms/AddDocumentDialog';
import { DeleteAuthoringDraftDialog } from '../components/eqms/DeleteAuthoringDraftDialog';
import { getAuthoringDrafts } from '../api/authoringApi';
import { useSession } from '@/hooks/useSession';
import type { AuthoringDraft } from '../types/authoring.types';
import { getControlledDocuments, transitionControlledDocument } from '../api/eqmsApi';
import {
  ControlledDocumentsTable,
} from '../components/eqms/ControlledDocumentsTable';
import { ControlledDocumentDetailDialog } from '../components/eqms/ControlledDocumentDetailDialog';
import { ElectronicSignatureDialog } from '../components/eqms/ElectronicSignatureDialog';
import { UpdateRequestsInbox } from '../components/eqms/update-requests/UpdateRequestsInbox';
import {
  EQMS_QUERY_KEYS,
  useControlledDocument,
  useControlledDocuments,
  useEqmsSummary,
} from '../hooks/useEqmsDocuments';
import { loadEqmsColumnFilters, persistEqmsColumnFilters } from '../utils/eqmsTableState';
import type {
  ControlledDocument,
  ControlledDocumentsFilters,
  EqmsAction,
  EqmsStatus,
  EqmsTransitionInput,
} from '../types/eqms.types';

const PAGE_LENGTH = 25;
const STATUS_OPTIONS: Array<{ value: EqmsStatus | 'all'; label: string }> = [
  { value: 'all', label: 'All states' },
  { value: 'draft', label: 'Draft' },
  { value: 'in_review', label: 'In review' },
  { value: 'approved', label: 'Approved' },
  { value: 'effective', label: 'Effective' },
  { value: 'obsolete', label: 'Obsolete' },
  // Hidden from "All states"; listed only on request.
  { value: 'discarded', label: 'Discarded' },
];

interface PendingSignature {
  action: EqmsAction;
  documentId: string;
  documentName: string;
  snapshotId?: string;
  idempotencyKey: string;
}

export function EqmsDashboardPage() {
  const { user } = useSession();
  const { t, i18n } = useTranslation('qms');
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const [page, setPage] = useState(0);
  const [status, setStatus] = useState<EqmsStatus | 'all'>('all');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [sortKey, setSortKey] = useState<ControlledDocumentsFilters['sortKey']>('updatedAt');
  const [sortDirection, setSortDirection] = useState<1 | -1>(-1);
  const [columnFilters, setColumnFilters] = useState<Record<string, string>>(loadEqmsColumnFilters);
  const [debouncedColumnSearch, setDebouncedColumnSearch] = useState('');
  const [selectedDocumentId, setSelectedDocumentId] = useState<string | null>(searchParams.get('document'));
  const [detailTab, setDetailTab] = useState<string | undefined>(searchParams.get('tab') ?? undefined);
  const [registerChooserOpen, setRegisterChooserOpen] = useState(false);
  const [draftToDelete, setDraftToDelete] = useState<AuthoringDraft | null>(null);
  const deleteDraftTriggerRef = useRef<HTMLButtonElement | null>(null);
  const addDocumentButtonRef = useRef<HTMLButtonElement | null>(null);
  const [pendingSignature, setPendingSignature] = useState<PendingSignature | null>(null);
  const signatureDocumentQuery = useControlledDocument(
    pendingSignature?.action === 'make_effective' ? pendingSignature.documentId : null,
  );

  useEffect(() => {
    const timeout = window.setTimeout(() => {
      setDebouncedSearch(search.trim());
      setPage(0);
    }, 300);
    return () => window.clearTimeout(timeout);
  }, [search]);

  const columnSearchText = useMemo(
    () => ['controlCode', 'name', 'department', 'ownerName']
      .map((key) => columnFilters[key]?.trim())
      .filter(Boolean)
      .join(' '),
    [columnFilters],
  );

  useEffect(() => {
    const timeout = window.setTimeout(() => {
      setDebouncedColumnSearch(columnSearchText);
      setPage(0);
    }, 300);
    return () => window.clearTimeout(timeout);
  }, [columnSearchText]);

  const combinedSearch = [debouncedSearch, debouncedColumnSearch].filter(Boolean).join(' ');
  const filters = useMemo<ControlledDocumentsFilters>(() => ({
    status,
    search: combinedSearch || undefined,
    pageNumber: page,
    pageLength: PAGE_LENGTH,
    sortKey,
    sortDirection,
  }), [combinedSearch, page, sortDirection, sortKey, status]);

  const summaryQuery = useEqmsSummary();
  const documentsQuery = useControlledDocuments(filters);
  const draftsQuery = useQuery({ queryKey: ['eqms', 'authoring-drafts'], queryFn: getAuthoringDrafts });

  const transitionMutation = useMutation({
    mutationFn: ({ id, input }: { id: string; input: EqmsTransitionInput }) =>
      transitionControlledDocument(id, input),
    onSuccess: async (result) => {
      toast.success(`Signed: ${result.document.controlCode} is now ${result.document.lifecycleStatus.replace('_', ' ')}`);
      setPendingSignature(null);
      setRegisterChooserOpen(false);
      setSelectedDocumentId(result.document.id);
      await queryClient.invalidateQueries({ queryKey: EQMS_QUERY_KEYS.root });
    },
  });

  const setColumnFilter = (key: string, value: string) => {
    setColumnFilters((current) => {
      const next = { ...current, [key]: value };
      persistEqmsColumnFilters(next);
      return next;
    });
  };

  const handleSort = (key: ControlledDocumentsFilters['sortKey']) => {
    if (key === sortKey) setSortDirection((direction) => direction === 1 ? -1 : 1);
    else {
      setSortKey(key);
      setSortDirection(1);
    }
    setPage(0);
  };

  const exportAll = async () => {
    const all: ControlledDocument[] = [];
    let exportPage = 0;
    let total: number;
    do {
      const batch = await getControlledDocuments({
        ...filters,
        pageNumber: exportPage,
        pageLength: 100,
      });
      all.push(...batch.documents);
      total = batch.total;
      exportPage += 1;
    } while (all.length < total);
    return all;
  };

  const openAction = (action: EqmsAction, documentId: string, documentName: string, snapshotId?: string) => {
    setPendingSignature({ action, documentId, documentName, snapshotId, idempotencyKey: crypto.randomUUID() });
  };

  const summary = summaryQuery.data;
  const canImportEffective = summary?.permissions?.canImportEffective === true;
  const canExcludeImportedFromTraining = summary?.permissions?.canExcludeFromTraining === true;
  const documents = documentsQuery.data?.documents || [];
  const workingDrafts = draftsQuery.data?.drafts.filter((draft) => !draft.documentId) || [];

  return (
    <div className="min-h-full bg-slate-50">
      <section className="overflow-hidden bg-slate-950 text-white">
        <div className="relative mx-auto max-w-[1600px] px-4 py-8 sm:px-6 lg:px-8">
          <div className="absolute -right-20 -top-24 h-72 w-72 rounded-full bg-teal-500/10 blur-3xl" />
          <div className="relative flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <div className="mb-3 flex items-center gap-2">
                <Badge className="border-teal-400/20 bg-teal-400/10 text-teal-200 hover:bg-teal-400/10">
                  <ShieldCheck className="mr-1 h-3.5 w-3.5" /> eQMS · Document Control
                </Badge>
                <span className="font-mono text-xs text-slate-400">PHASE 1</span>
              </div>
              <h1 className="text-3xl font-semibold tracking-tight sm:text-4xl">Quality evidence, under control.</h1>
              <p className="mt-3 max-w-2xl text-sm leading-6 text-slate-300 sm:text-base">
                One command center for controlled documents, accountable approvals, electronic signatures,
                and an audit trail designed to remain evidence.
              </p>
            </div>
            <Button
              ref={addDocumentButtonRef}
              onClick={() => setRegisterChooserOpen(true)}
              className="w-fit bg-teal-500 text-slate-950 hover:bg-teal-400"
            >
              <FilePlus2 className="mr-2 h-4 w-4" /> {t('authoring.add')}
            </Button>
          </div>
        </div>
      </section>

      <main className="mx-auto max-w-[1600px] space-y-6 px-4 py-6 sm:px-6 lg:px-8">
        {draftsQuery.isError && <div role="alert" className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-900"><p>{t('authoring.loadFailed')}</p><Button variant="outline" size="sm" onClick={() => { void draftsQuery.refetch(); }}>{t('authoring.retry')}</Button></div>}
        {workingDrafts.length > 0 && <section className="rounded-xl border border-teal-100 bg-white p-5">
          <h2 className="font-semibold text-slate-900">{t('authoring.drafts')}</h2><p className="mt-1 text-xs text-slate-500">{t('authoring.draftsHint')}</p>
          <div className="mt-4 grid gap-3 sm:grid-cols-2 xl:grid-cols-3">{workingDrafts.map((draft) => (
            <article key={draft.id} aria-label={draft.title || t('authoring.untitled')} className="flex items-start gap-2 rounded-lg border border-slate-200 p-3">
              <Link to={`/eqms/authoring/${draft.id}`} className="min-w-0 flex-1 rounded-sm hover:text-teal-800 focus-visible:outline-teal-600">
                <p className="truncate text-sm font-medium">{draft.title || t('authoring.untitled')}</p>
                <p className="mt-1 text-xs text-slate-400">{t('authoring.updated', { date: new Date(draft.updatedAt).toLocaleString(i18n.language, { timeZone: 'America/Montreal' }) })}</p>
              </Link>
              <Button variant="ghost" size="sm" className="shrink-0 text-red-700 hover:bg-red-50 hover:text-red-800"
                aria-label={t('authoring.deleteNamed', { name: draft.title || t('authoring.untitled') })}
                onClick={event => { deleteDraftTriggerRef.current = event.currentTarget; setDraftToDelete(draft); }}>
                <Trash2 aria-hidden="true" className="mr-1 h-3.5 w-3.5" />{t('authoring.delete')}
              </Button>
            </article>
          ))}</div>
        </section>}
        {draftToDelete && <DeleteAuthoringDraftDialog draft={draftToDelete} onClose={() => setDraftToDelete(null)}
          onRestoreFocus={() => {
            const trigger = deleteDraftTriggerRef.current;
            (trigger?.isConnected ? trigger : addDocumentButtonRef.current)?.focus();
          }} />}

        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
          <Link to="/eqms" className="group rounded-xl border border-teal-200 bg-teal-50 p-4">
            <div className="flex items-center justify-between">
              <FolderLock className="h-5 w-5 text-teal-700" />
              <ArrowRight className="h-4 w-4 text-teal-700 transition-transform group-hover:translate-x-1" />
            </div>
            <div className="mt-3 font-medium text-teal-950">Controlled documents</div>
            <div className="mt-1 text-xs text-teal-800">Lifecycle, signatures, and audit evidence</div>
          </Link>
          <Link to="/qms" className="group rounded-xl border border-slate-200 bg-white p-4">
            <div className="flex items-center justify-between">
              <AlertCircle className="h-5 w-5 text-amber-600" />
              <ArrowRight className="h-4 w-4 text-slate-400 transition-transform group-hover:translate-x-1" />
            </div>
            <div className="mt-3 font-medium text-slate-900">Quality events & CAPA</div>
            <div className="mt-1 text-xs text-slate-500">Existing QMS issue, CAPA, and change-control workflows</div>
          </Link>
          <Link to="/eqms/training" className="group rounded-xl border border-slate-200 bg-white p-4">
            <div className="flex items-center justify-between">
              <GraduationCap className="h-5 w-5 text-teal-700" />
              <ArrowRight className="h-4 w-4 text-slate-400 transition-transform group-hover:translate-x-1" />
            </div>
            <div className="mt-3 font-medium text-slate-900">Training & competency</div>
            <div className="mt-1 text-xs text-slate-500">SOP assignments, assessments, signatures, and compliance</div>
          </Link>
          <Link to="/eqms/validation" className="group rounded-xl border border-slate-200 bg-white p-4">
            <div className="flex items-center justify-between">
              <PackageCheck className="h-5 w-5 text-cyan-700" />
              <ArrowRight className="h-4 w-4 text-slate-400 transition-transform group-hover:translate-x-1" />
            </div>
            <div className="mt-3 font-medium text-slate-900">Validation & readiness</div>
            <div className="mt-1 text-xs text-slate-500">Requirement evidence, reviews, and frozen inspection packages</div>
          </Link>
        </div>

        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
          {[
            { label: 'Controlled records', value: summary?.total, icon: FileCheck2, tone: 'text-slate-700 bg-slate-100' },
            { label: 'Awaiting quality action', value: summary?.awaitingAction, icon: BookOpenCheck, tone: 'text-amber-700 bg-amber-50' },
            { label: 'Effective', value: summary?.byStatus.effective, icon: ShieldCheck, tone: 'text-teal-700 bg-teal-50' },
            { label: 'Immutable evidence', value: 'ON', icon: FolderLock, tone: 'text-blue-700 bg-blue-50' },
          ].map((metric) => (
            <Card key={metric.label} className="border-slate-200 shadow-sm">
              <CardContent className="flex items-center justify-between p-5">
                <div>
                  <div className="text-xs font-medium uppercase tracking-wide text-slate-400">{metric.label}</div>
                  {summaryQuery.isLoading && typeof metric.value !== 'string'
                    ? <Skeleton className="mt-2 h-8 w-12" />
                    : <div className="mt-1 text-2xl font-semibold tabular-nums text-slate-950">
                      {summaryQuery.isError && typeof metric.value !== 'string' ? '—' : metric.value ?? 0}
                    </div>}
                </div>
                <div className={`rounded-xl p-2.5 ${metric.tone}`}><metric.icon className="h-5 w-5" /></div>
              </CardContent>
            </Card>
          ))}
        </div>

        <UpdateRequestsInbox onOpenDocument={(id) => { setDetailTab('update-requests'); setSelectedDocumentId(id); }} />

        {(documentsQuery.isError || summaryQuery.isError) && (
          <div role="alert" className="flex flex-col gap-3 rounded-xl border border-red-200 bg-red-50 p-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="font-medium text-red-900">
                {documentsQuery.isError ? 'Unable to load controlled documents' : 'Unable to load document summary'}
              </p>
              <p className="mt-1 text-sm text-red-800">
                The document service could not complete the request. Retry to load the register and summary.
              </p>
            </div>
            <Button variant="outline" disabled={documentsQuery.isFetching || summaryQuery.isFetching}
              onClick={() => { void Promise.all([documentsQuery.refetch(), summaryQuery.refetch()]); }}>
              Retry loading
            </Button>
          </div>
        )}

        <div className="flex flex-col gap-3 rounded-xl border border-slate-200 bg-white p-4 sm:flex-row sm:items-center">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
            <Input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search control ID, document, department, or owner…"
              className="pl-9"
            />
          </div>
          <Select value={status} onValueChange={(value) => { setStatus(value as EqmsStatus | 'all'); setPage(0); }}>
            <SelectTrigger className="w-full sm:w-44">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {STATUS_OPTIONS.map((option) => (
                <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {!documentsQuery.isError && <ControlledDocumentsTable
          documents={documents}
          total={documentsQuery.data?.total || 0}
          page={page}
          pageLength={PAGE_LENGTH}
          sortKey={sortKey}
          sortDirection={sortDirection}
          columnFilters={columnFilters}
          isLoading={documentsQuery.isLoading}
          isRefreshing={documentsQuery.isFetching}
          onColumnFilterChange={setColumnFilter}
          onClearColumnFilters={() => {
            setColumnFilters({});
            persistEqmsColumnFilters({});
          }}
          onSort={handleSort}
          onPageChange={setPage}
          onRowClick={(document) => { setDetailTab(document.pendingUpdateRequests ? 'update-requests' : undefined); setSelectedDocumentId(document.id); }}
          onRefresh={() => documentsQuery.refetch()}
          onExportAll={exportAll}
        />}
      </main>

      <ControlledDocumentDetailDialog
        documentId={selectedDocumentId}
        currentUserId={user?.pg_id || user?._id}
        initialTab={detailTab}
        onClose={() => { setSelectedDocumentId(null); setDetailTab(undefined); if (searchParams.has('document')) setSearchParams({}); }}
        onAction={openAction}
      />

      {pendingSignature && (
        <ElectronicSignatureDialog
          open
          action={pendingSignature.action}
          documentName={pendingSignature.documentName}
          defaultUsername={user?.username || user?.email || ''}
          canExcludeFromTraining={pendingSignature.action === 'register'
            ? canExcludeImportedFromTraining
            : signatureDocumentQuery.data?.permissions?.canExcludeFromTraining === true}
          canImportEffective={pendingSignature.action === 'register' && canImportEffective}
          isPending={transitionMutation.isPending}
          onOpenChange={(open) => !open && setPendingSignature(null)}
          onConfirm={(input) => transitionMutation.mutateAsync({ id: pendingSignature.documentId, input: { ...input, ...(pendingSignature.snapshotId ? { snapshotId: pendingSignature.snapshotId, idempotencyKey: pendingSignature.idempotencyKey } : {}) } }).then(() => undefined)}
        />
      )}

      <AddDocumentDialog open={registerChooserOpen} onOpenChange={setRegisterChooserOpen}
        onRegister={(id, name) => openAction('register', id, name)} />
    </div>
  );
}
